// LookupPage — replaces the legacy `look_up.php` "System Lookup" page.
//
// Two pill tabs (cancellations-style):
//   - Bookings & Receipts: registration search → parallel fetch from
//     /bookings (?reg=), /pending (?q=), /receipts (?registration=).
//     Sources with hits surface as sub-tabs (Booking / Pending /
//     Receipts), each with the matched record's key fields + a deep
//     link to the relevant detail page.
//   - Customers: type-ahead autocomplete against /customers (?search=),
//     selected row populates a sidebar (personal / address / banking)
//     plus a stats row + bookings history table.
//
// Tab + selection state lives in URL params so deep links survive a
// refresh:
//   ?tab=bookings|customers
//   ?reg=<plate>            (bookings tab)
//   ?customer_uuid=<uuid>   (customers tab)
//
// All data comes from existing endpoints — no new server routes
// required. The page is read-only: edits route through the regular
// detail pages via per-row "Open" links.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CalendarCheck,
  ExternalLink,
  FileText,
  Hourglass,
  MessageSquare,
  Pencil,
  Search as SearchIcon,
  User,
  Users,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Input } from '../../components/form/Input';
import { StatusBadge } from '../../components/ui/StatusBadge/StatusBadge';
import { Table, type Column } from '../../components/ui/Table/Table';
import { Spinner } from '../../components/ui/Spinner/Spinner';
import { Badge, type BadgeVariant } from '../../components/ui/Badge/Badge';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { useDebounce } from '../../hooks/useDebounce';
import {
  useBookingSearch,
  type BookingSearchResult,
} from '../../hooks/useBookingSearch';
import { useCustomerDetail } from '../customers/hooks/useCustomerDetail';
import { useCustomerBookings } from '../customers/hooks/useCustomerBookings';
import type {
  BookingListResponse,
  BookingStatus,
  BookingSummary,
  CustomerBookingHistoryItem,
  CustomerListResponse,
  CustomerSummary,
  PendingListResponse,
  PendingStatus,
  PendingSummary,
  ReceiptListResponse,
  ReceiptStatus,
  ReceiptSummary,
} from '../../types/api';
import styles from './LookupPage.module.css';

type Tab = 'bookings' | 'customers' | 'notes';
type SourceKey = 'booking' | 'pending' | 'receipts';

const BOOKING_STATUS_MAP: Record<BookingStatus, BadgeVariant> = {
  pending: 'warning',
  in_progress: 'info',
  completed: 'success',
  cancelled: 'danger',
  cancellation_requested: 'warning',
};

const PENDING_STATUS_MAP: Record<PendingStatus, BadgeVariant> = {
  pending: 'warning',
  final_notice: 'danger',
  booked: 'success',
  cancelled: 'muted',
};

const RECEIPT_STATUS_MAP: Record<ReceiptStatus, BadgeVariant> = {
  pending_review: 'warning',
  approved: 'success',
  rejected: 'danger',
  archived: 'muted',
};

const RECEIPT_STATUS_LABELS: Record<ReceiptStatus, string> = {
  pending_review: 'Pending review',
  approved: 'Approved',
  rejected: 'Rejected',
  archived: 'Archived',
};

const CUSTOMER_AUTOCOMPLETE_LIMIT = 10;
const CUSTOMER_BOOKINGS_PAGE_SIZE = 20;
const REGISTRATION_QUERY_LIMIT = 20;

function normaliseReg(v: string): string {
  return v.replace(/\s+/g, '').toUpperCase();
}

function formatMoney(n: number | null): string {
  if (n === null) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface LookupResults {
  booking: BookingSummary | null;
  pending: PendingSummary | null;
  receipts: ReceiptSummary[];
}

async function runRegistrationLookup(reg: string): Promise<LookupResults> {
  const normalised = normaliseReg(reg);
  if (normalised.length === 0) {
    return { booking: null, pending: null, receipts: [] };
  }
  const [bookingsRes, pendingRes, receiptsRes] = await Promise.all([
    apiClient
      .get<BookingListResponse>(endpoints.bookings.list, {
        params: { reg: normalised, limit: 1, sort: 'date_desc' },
      })
      .then((r) => r.data)
      .catch(() => ({
        bookings: [] as BookingSummary[],
        pagination: { page: 1, limit: 1, total: 0, totalPages: 1 },
      })),
    apiClient
      .get<PendingListResponse>(endpoints.pending.list, {
        params: { q: normalised, limit: 1 },
      })
      .then((r) => r.data)
      .catch(() => ({
        pending_bookings: [] as PendingSummary[],
        pagination: { page: 1, limit: 1, total: 0, totalPages: 1 },
      })),
    apiClient
      .get<ReceiptListResponse>(endpoints.receipts.list, {
        params: { registration: normalised, limit: REGISTRATION_QUERY_LIMIT },
      })
      .then((r) => r.data)
      .catch(() => ({
        receipts: [] as ReceiptSummary[],
        pagination: { page: 1, limit: REGISTRATION_QUERY_LIMIT, total: 0, totalPages: 1 },
      })),
  ]);
  return {
    booking: bookingsRes.bookings[0] ?? null,
    pending: pendingRes.pending_bookings[0] ?? null,
    receipts: receiptsRes.receipts,
  };
}

async function searchCustomers(term: string): Promise<CustomerSummary[]> {
  const trimmed = term.trim();
  if (trimmed.length < 2) return [];
  const res = await apiClient.get<CustomerListResponse>(endpoints.customers.list, {
    params: { search: trimmed, limit: CUSTOMER_AUTOCOMPLETE_LIMIT },
  });
  return res.data?.customers ?? [];
}

export function LookupPage(): JSX.Element {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const tabParam = params.get('tab');
  const tab: Tab =
    tabParam === 'customers' ? 'customers' : tabParam === 'notes' ? 'notes' : 'bookings';
  const regParam = params.get('reg') ?? '';
  const customerUuid = params.get('customer_uuid') ?? '';

  function setParam(key: string, value: string | null): void {
    const next = new URLSearchParams(params);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: false });
  }

  function selectTab(next: Tab): void {
    const params2 = new URLSearchParams();
    if (next !== 'bookings') params2.set('tab', next);
    setParams(params2, { replace: false });
  }

  // ----- Bookings & Receipts tab -------------------------------------------

  const [regInput, setRegInput] = useState(regParam);
  useEffect(() => {
    setRegInput(regParam);
  }, [regParam]);

  const lookupQuery = useQuery({
    queryKey: ['lookup', 'registration', regParam] as const,
    queryFn: () => runRegistrationLookup(regParam),
    enabled: tab === 'bookings' && regParam.length > 0,
    staleTime: 30_000,
  });

  const results = lookupQuery.data ?? null;
  const sources: SourceKey[] = useMemo(() => {
    if (!results) return [];
    const out: SourceKey[] = [];
    if (results.booking) out.push('booking');
    if (results.pending) out.push('pending');
    if (results.receipts.length > 0) out.push('receipts');
    return out;
  }, [results]);

  const [activeSource, setActiveSource] = useState<SourceKey | null>(null);
  useEffect(() => {
    // Auto-select the first available source when results change.
    if (sources.length === 0) {
      setActiveSource(null);
    } else if (!activeSource || !sources.includes(activeSource)) {
      setActiveSource(sources[0] ?? null);
    }
  }, [sources, activeSource]);

  function submitRegistration(e: React.FormEvent): void {
    e.preventDefault();
    const trimmed = regInput.trim();
    if (trimmed.length === 0) return;
    setParam('reg', trimmed);
  }

  // ----- Customers tab ------------------------------------------------------

  const [customerTerm, setCustomerTerm] = useState('');
  const [acOpen, setAcOpen] = useState(false);
  const debounced = useDebounce(customerTerm, 300);
  const acWrapRef = useRef<HTMLDivElement | null>(null);

  const acQuery = useQuery({
    queryKey: ['lookup', 'customers', 'search', debounced] as const,
    queryFn: () => searchCustomers(debounced),
    enabled: tab === 'customers' && debounced.trim().length >= 2,
    staleTime: 30_000,
  });

  // Close the autocomplete on outside click. The dropdown itself lives
  // in a portal under document.body (so it can escape the Card's
  // `overflow: hidden`), so a "contains" check against the input wrapper
  // alone would close it on every option click — also accept clicks on
  // any ancestor tagged with `data-lookup-ac-dropdown`.
  useEffect(() => {
    if (!acOpen) return;
    function onDoc(e: MouseEvent): void {
      const target = e.target as HTMLElement | null;
      const node = acWrapRef.current;
      if (!target) return;
      if (node && node.contains(target)) return;
      if (target.closest('[data-lookup-ac-dropdown]') !== null) return;
      setAcOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [acOpen]);

  function selectCustomer(c: CustomerSummary): void {
    setAcOpen(false);
    setCustomerTerm('');
    setParam('customer_uuid', c.uuid);
  }

  const customerDetail = useCustomerDetail(customerUuid);
  const customerBookingsQuery = useCustomerBookings(customerUuid, 1);
  const bookingsHistory = customerBookingsQuery.data?.bookings ?? [];
  // Server returns `{ bookings, total }` for /customers/:uuid/bookings (see
  // server/src/modules/customers/customers.service.ts:listCustomerBookings).
  // The shared CustomerBookingHistoryResponse type incorrectly declares a
  // `pagination` field — read both shapes defensively until that type is
  // corrected, then fall back to the in-page bookings length so the stat
  // tile never crashes the page on a missing field.
  const bookingsRaw = customerBookingsQuery.data as
    | { bookings?: unknown[]; total?: number; pagination?: { total?: number } }
    | undefined;
  const bookingsTotal =
    bookingsRaw?.total ?? bookingsRaw?.pagination?.total ?? bookingsHistory.length;

  const customerStats = useMemo(() => {
    const completed = bookingsHistory.filter((b) => b.status === 'completed').length;
    const pending = bookingsHistory.filter((b) => b.status === 'pending').length;
    const cancelled = bookingsHistory.filter((b) => b.status === 'cancelled').length;
    const revenue = bookingsHistory
      .filter((b) => b.status === 'completed')
      .reduce((sum, b) => sum + b.price, 0);
    return { completed, pending, cancelled, revenue };
  }, [bookingsHistory]);

  const historyColumns: Column<CustomerBookingHistoryItem>[] = useMemo(
    () => [
      {
        key: 'car_registration',
        header: 'Reg',
        render: (_v, row) => (
          <span className={styles.regChip}>{row.car_registration}</span>
        ),
      },
      {
        key: 'car_make_model',
        header: 'Vehicle',
        render: (_v, row) => row.car_make_model || '—',
      },
      {
        key: 'date',
        header: 'Date',
        render: (_v, row) => formatDate(row.date),
      },
      {
        key: 'price',
        header: 'Amount',
        align: 'right',
        render: (_v, row) => formatMoney(row.price),
      },
      {
        key: 'status',
        header: 'Status',
        render: (_v, row) => (
          <StatusBadge
            status={row.status}
            statusMap={BOOKING_STATUS_MAP}
            formatLabel={(s) => s.replaceAll('_', ' ')}
          />
        ),
      },
      {
        key: 'unique_id',
        header: '',
        align: 'right',
        render: (_v, row) => (
          <Button
            size="sm"
            variant="ghost"
            icon={ExternalLink}
            onClick={() => navigate(`/collections/bookings/${encodeURIComponent(row.uuid)}`)}
            aria-label="Open booking"
          >
            Open
          </Button>
        ),
      },
    ],
    [navigate],
  );

  // ----- Booking Search (free-text notes) tab ------------------------------

  const [notesInput, setNotesInput] = useState('');
  const [notesSubmitted, setNotesSubmitted] = useState('');
  const [includeCompleted, setIncludeCompleted] = useState(false);

  const notesSearchQuery = useBookingSearch({
    q: notesSubmitted,
    includeCompleted,
    enabled: tab === 'notes' && notesSubmitted.trim().length >= 2,
  });

  function submitNotesSearch(e: React.FormEvent): void {
    e.preventDefault();
    const trimmed = notesInput.trim();
    if (trimmed.length < 2) return;
    setNotesSubmitted(trimmed);
  }

  // ----- Render -------------------------------------------------------------

  return (
    <div className={styles.page}>
      <PageHeader
        title="System Lookup"
        subtitle="Search bookings, pending records, receipts and customer accounts"
      />

      <div role="tablist" className={styles.tabs} aria-label="Lookup tabs">
        <button
          role="tab"
          type="button"
          aria-selected={tab === 'bookings'}
          tabIndex={tab === 'bookings' ? 0 : -1}
          className={tab === 'bookings' ? styles.tabActive : styles.tab}
          onClick={() => selectTab('bookings')}
        >
          <CalendarCheck size={14} aria-hidden /> Bookings &amp; Receipts
        </button>
        <button
          role="tab"
          type="button"
          aria-selected={tab === 'customers'}
          tabIndex={tab === 'customers' ? 0 : -1}
          className={tab === 'customers' ? styles.tabActive : styles.tab}
          onClick={() => selectTab('customers')}
        >
          <Users size={14} aria-hidden /> Customers
        </button>
        <button
          role="tab"
          type="button"
          aria-selected={tab === 'notes'}
          tabIndex={tab === 'notes' ? 0 : -1}
          className={tab === 'notes' ? styles.tabActive : styles.tab}
          onClick={() => selectTab('notes')}
        >
          <MessageSquare size={14} aria-hidden /> Booking Search
        </button>
      </div>

      {tab === 'bookings' ? (
        <BookingsLookupTab
          regInput={regInput}
          setRegInput={setRegInput}
          regSubmitted={regParam}
          isLoading={lookupQuery.isFetching}
          isError={lookupQuery.isError}
          results={results}
          sources={sources}
          activeSource={activeSource}
          setActiveSource={setActiveSource}
          onSubmit={submitRegistration}
          navigate={navigate}
        />
      ) : tab === 'customers' ? (
        <CustomersLookupTab
          term={customerTerm}
          setTerm={setCustomerTerm}
          open={acOpen}
          setOpen={setAcOpen}
          acWrapRef={acWrapRef}
          results={acQuery.data ?? []}
          isLoading={acQuery.isFetching}
          isEnabled={debounced.trim().length >= 2}
          onSelect={selectCustomer}
          customer={customerDetail.data ?? null}
          customerLoading={customerDetail.isLoading}
          stats={customerStats}
          bookings={bookingsHistory}
          bookingsTotal={bookingsTotal}
          bookingsLoading={customerBookingsQuery.isLoading}
          historyColumns={historyColumns}
          navigate={navigate}
        />
      ) : (
        <BookingSearchTab
          input={notesInput}
          setInput={setNotesInput}
          submitted={notesSubmitted}
          includeCompleted={includeCompleted}
          setIncludeCompleted={setIncludeCompleted}
          isLoading={notesSearchQuery.isFetching}
          isError={notesSearchQuery.isError}
          results={notesSearchQuery.data?.results ?? null}
          onSubmit={submitNotesSearch}
          navigate={navigate}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bookings & Receipts tab
// ---------------------------------------------------------------------------

interface BookingsLookupTabProps {
  regInput: string;
  setRegInput: (v: string) => void;
  regSubmitted: string;
  isLoading: boolean;
  isError: boolean;
  results: LookupResults | null;
  sources: SourceKey[];
  activeSource: SourceKey | null;
  setActiveSource: (s: SourceKey) => void;
  onSubmit: (e: React.FormEvent) => void;
  navigate: ReturnType<typeof useNavigate>;
}

function BookingsLookupTab(props: BookingsLookupTabProps): JSX.Element {
  const {
    regInput,
    setRegInput,
    regSubmitted,
    isLoading,
    isError,
    results,
    sources,
    activeSource,
    setActiveSource,
    onSubmit,
    navigate,
  } = props;

  const noHits = regSubmitted.length > 0 && !isLoading && results !== null && sources.length === 0;

  return (
    <>
      <Card>
        <form onSubmit={onSubmit} className={styles.searchRow}>
          <Input
            label="Search by car registration"
            placeholder="Enter registration number…"
            value={regInput}
            onChange={(e) => setRegInput(e.target.value.toUpperCase())}
            autoFocus
            aria-label="Vehicle registration"
          />
          <Button
            type="submit"
            variant="primary"
            icon={SearchIcon}
            disabled={regInput.trim().length === 0}
          >
            Search
          </Button>
        </form>
      </Card>

      {regSubmitted.length === 0 ? (
        <Card>
          <div className={styles.empty}>
            <SearchIcon size={32} aria-hidden />
            <h3>Enter a registration to begin</h3>
            <p>We'll check active bookings, the pending queue, and COVD receipts.</p>
          </div>
        </Card>
      ) : null}

      {isLoading ? (
        <Card>
          <div className={styles.empty}>
            <Spinner size="md" />
            <p style={{ marginTop: '0.5rem' }}>Searching…</p>
          </div>
        </Card>
      ) : null}

      {isError ? (
        <Card>
          <div className={styles.empty}>
            <h3>Lookup failed</h3>
            <p>The search request failed. Please try again.</p>
          </div>
        </Card>
      ) : null}

      {noHits ? (
        <Card>
          <div className={styles.empty}>
            <h3>No records found</h3>
            <p>
              No matches for <span className={styles.regChip}>{regSubmitted}</span>{' '}
              across bookings, pending, or receipts.
            </p>
          </div>
        </Card>
      ) : null}

      {results !== null && sources.length > 0 ? (
        <>
          <div className={styles.summary}>
            <SearchIcon size={14} aria-hidden />
            <span>Results for</span>
            <span className={styles.regChip}>{regSubmitted}</span>
            {results.booking ? (
              <span className={`${styles.sourceChip} ${styles.sourceChipBooking}`}>
                <CalendarCheck size={12} aria-hidden /> Booking
              </span>
            ) : null}
            {results.pending ? (
              <span className={`${styles.sourceChip} ${styles.sourceChipPending}`}>
                <Hourglass size={12} aria-hidden /> Pending
              </span>
            ) : null}
            {results.receipts.length > 0 ? (
              <span className={`${styles.sourceChip} ${styles.sourceChipReceipts}`}>
                <FileText size={12} aria-hidden /> {results.receipts.length} Receipt
                {results.receipts.length === 1 ? '' : 's'}
              </span>
            ) : null}
          </div>

          <Card flush>
            {sources.length > 1 ? (
              <div role="tablist" className={styles.subTabs} aria-label="Result sources">
                {sources.includes('booking') ? (
                  <button
                    role="tab"
                    type="button"
                    aria-selected={activeSource === 'booking'}
                    className={
                      activeSource === 'booking' ? styles.subTabActive : styles.subTab
                    }
                    onClick={() => setActiveSource('booking')}
                  >
                    <CalendarCheck size={12} aria-hidden /> Booking
                  </button>
                ) : null}
                {sources.includes('pending') ? (
                  <button
                    role="tab"
                    type="button"
                    aria-selected={activeSource === 'pending'}
                    className={
                      activeSource === 'pending' ? styles.subTabActive : styles.subTab
                    }
                    onClick={() => setActiveSource('pending')}
                  >
                    <Hourglass size={12} aria-hidden /> Pending
                  </button>
                ) : null}
                {sources.includes('receipts') ? (
                  <button
                    role="tab"
                    type="button"
                    aria-selected={activeSource === 'receipts'}
                    className={
                      activeSource === 'receipts' ? styles.subTabActive : styles.subTab
                    }
                    onClick={() => setActiveSource('receipts')}
                  >
                    <FileText size={12} aria-hidden /> Receipts
                    <span className={styles.subTabBadge}>{results.receipts.length}</span>
                  </button>
                ) : null}
              </div>
            ) : null}

            {activeSource === 'booking' && results.booking ? (
              <BookingPanel booking={results.booking} navigate={navigate} />
            ) : null}
            {activeSource === 'pending' && results.pending ? (
              <PendingPanel pending={results.pending} navigate={navigate} />
            ) : null}
            {activeSource === 'receipts' && results.receipts.length > 0 ? (
              <ReceiptsPanel receipts={results.receipts} navigate={navigate} />
            ) : null}
          </Card>
        </>
      ) : null}
    </>
  );
}

interface BookingPanelProps {
  booking: BookingSummary;
  navigate: ReturnType<typeof useNavigate>;
}

function BookingPanel({ booking, navigate }: BookingPanelProps): JSX.Element {
  const isCancelled = booking.status === 'cancelled';
  return (
    <div className={styles.recordPanel}>
      <div className={styles.fieldGrid}>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Customer</span>
          <span className={styles.fieldValue}>{booking.name}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Phone</span>
          <span className={styles.fieldValue}>{booking.phone}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Email</span>
          <span className={styles.fieldValue}>{booking.email ?? '—'}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Vehicle</span>
          <span className={styles.fieldValue}>{booking.car_make_model}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Registration</span>
          <span className={styles.fieldValue}>
            <span className={styles.regChip}>{booking.car_registration}</span>
          </span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Driver</span>
          <span className={styles.fieldValue}>{booking.driver_name ?? '—'}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Collection date</span>
          <span className={styles.fieldValue}>{formatDate(booking.date)}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Time frame</span>
          <span className={styles.fieldValue}>{booking.timeframe}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Payment</span>
          <span className={styles.fieldValue}>{booking.payment_mode_name ?? '—'}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Source</span>
          <span className={styles.fieldValue}>{booking.source_name ?? '—'}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Amount</span>
          <span className={styles.fieldValue}>{formatMoney(booking.price)}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Status</span>
          <span className={styles.fieldValue}>
            <StatusBadge
              status={booking.status}
              statusMap={BOOKING_STATUS_MAP}
              formatLabel={(s) => s.replaceAll('_', ' ')}
            />
          </span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Reference</span>
          <span className={styles.fieldValueMono}>{booking.reference}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Unique ID</span>
          <span className={styles.fieldValueMono}>{booking.unique_id}</span>
        </div>
      </div>

      {booking.notes ? (
        <>
          <div className={styles.fieldLabel} style={{ marginTop: '1rem' }}>
            Notes
          </div>
          <div className={styles.notesBlock}>{booking.notes}</div>
        </>
      ) : null}

      {isCancelled ? (
        <div className={styles.cancelBlock}>
          <h4>Cancellation details</h4>
          {booking.cancelled_at ? (
            <p>
              <strong>Cancelled at:</strong> {formatDateTime(booking.cancelled_at)}
            </p>
          ) : null}
          {booking.cancellation_reason ? (
            <p>
              <strong>Reason:</strong> {booking.cancellation_reason}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className={styles.actionsRow}>
        <Button
          variant="primary"
          icon={ExternalLink}
          onClick={() => navigate(`/collections/bookings/${encodeURIComponent(booking.uuid)}`)}
        >
          Open booking
        </Button>
        {booking.receipt_uuid ? (
          <Button
            variant="secondary"
            icon={FileText}
            onClick={() => navigate(`/collections/receipts/${encodeURIComponent(booking.receipt_uuid!)}`)}
          >
            Linked receipt
          </Button>
        ) : null}
      </div>
    </div>
  );
}

interface PendingPanelProps {
  pending: PendingSummary;
  navigate: ReturnType<typeof useNavigate>;
}

function PendingPanel({ pending, navigate }: PendingPanelProps): JSX.Element {
  return (
    <div className={styles.recordPanel}>
      <div className={styles.fieldGrid}>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Customer</span>
          <span className={styles.fieldValue}>{pending.name}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Phone</span>
          <span className={styles.fieldValue}>{pending.phone}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Email</span>
          <span className={styles.fieldValue}>{pending.email ?? '—'}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Vehicle</span>
          <span className={styles.fieldValue}>{pending.car_make_model}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Registration</span>
          <span className={styles.fieldValue}>
            <span className={styles.regChip}>{pending.car_registration}</span>
          </span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Source</span>
          <span className={styles.fieldValue}>{pending.source_name ?? '—'}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Status</span>
          <span className={styles.fieldValue}>
            <StatusBadge
              status={pending.status}
              statusMap={PENDING_STATUS_MAP}
              formatLabel={(s) => s.replaceAll('_', ' ')}
            />
          </span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Reference</span>
          <span className={styles.fieldValueMono}>{pending.reference}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Created</span>
          <span className={styles.fieldValue}>{formatDateTime(pending.created_at)}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Price</span>
          <span className={styles.fieldValue}>{formatMoney(pending.price)}</span>
        </div>
      </div>

      {pending.notes ? (
        <>
          <div className={styles.fieldLabel} style={{ marginTop: '1rem' }}>
            Notes
          </div>
          <div className={styles.notesBlock}>{pending.notes}</div>
        </>
      ) : null}

      <div className={styles.actionsRow}>
        <Button
          variant="primary"
          icon={ExternalLink}
          onClick={() => navigate(`/collections/pending/${encodeURIComponent(pending.enc_id)}`)}
        >
          Open pending record
        </Button>
        {pending.converted_booking ? (
          <Button
            variant="secondary"
            icon={ExternalLink}
            onClick={() =>
              navigate(`/collections/bookings/${encodeURIComponent(pending.converted_booking!.uuid)}`)
            }
          >
            Converted booking
          </Button>
        ) : null}
      </div>
    </div>
  );
}

interface ReceiptsPanelProps {
  receipts: ReceiptSummary[];
  navigate: ReturnType<typeof useNavigate>;
}

function ReceiptsPanel({ receipts, navigate }: ReceiptsPanelProps): JSX.Element {
  const columns: Column<ReceiptSummary>[] = [
    {
      key: 'reference',
      header: 'Reference',
      render: (_v, row) => (
        <span className={styles.fieldValueMono}>{row.reference ?? row.unique_id}</span>
      ),
    },
    {
      key: 'receipt_date',
      header: 'Date',
      render: (_v, row) => formatDate(row.receipt_date),
    },
    {
      key: 'customer_name',
      header: 'Customer',
      render: (_v, row) => row.customer_name,
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (_v, row) => formatMoney(row.amount),
    },
    {
      key: 'status',
      header: 'Status',
      render: (_v, row) => (
        <StatusBadge
          status={row.status}
          statusMap={RECEIPT_STATUS_MAP}
          formatLabel={(s) => RECEIPT_STATUS_LABELS[s] ?? s}
        />
      ),
    },
    {
      key: 'enc_id',
      header: '',
      align: 'right',
      render: (_v, row) => (
        <Button
          size="sm"
          variant="ghost"
          icon={ExternalLink}
          onClick={() => navigate(`/collections/receipts/${encodeURIComponent(row.enc_id)}`)}
          aria-label="Open receipt"
        >
          Open
        </Button>
      ),
    },
  ];
  return (
    <div className={styles.recordPanel}>
      <Table
        columns={columns}
        data={receipts}
        keyField={(r) => r.enc_id}
        ariaLabel="Receipts for the registration"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Customers tab
// ---------------------------------------------------------------------------

interface DropdownRect {
  top: number;
  left: number;
  width: number;
}

interface CustomersLookupTabProps {
  term: string;
  setTerm: (v: string) => void;
  open: boolean;
  setOpen: (v: boolean) => void;
  acWrapRef: React.RefObject<HTMLDivElement>;
  results: CustomerSummary[];
  isLoading: boolean;
  isEnabled: boolean;
  onSelect: (c: CustomerSummary) => void;
  customer: CustomerSummary | null;
  customerLoading: boolean;
  stats: { completed: number; pending: number; cancelled: number; revenue: number };
  bookings: CustomerBookingHistoryItem[];
  bookingsTotal: number;
  bookingsLoading: boolean;
  historyColumns: Column<CustomerBookingHistoryItem>[];
  navigate: ReturnType<typeof useNavigate>;
}

function CustomersLookupTab(props: CustomersLookupTabProps): JSX.Element {
  const {
    term,
    setTerm,
    open,
    setOpen,
    acWrapRef,
    results,
    isLoading,
    isEnabled,
    onSelect,
    customer,
    customerLoading,
    stats,
    bookings,
    bookingsTotal,
    bookingsLoading,
    historyColumns,
    navigate,
  } = props;

  // Track the wrapper's screen position so the portalled dropdown can sit
  // immediately below the input. Recompute on open + on scroll/resize so the
  // panel follows the input if the user scrolls the surrounding card.
  const [rect, setRect] = useState<DropdownRect | null>(null);
  useLayoutEffect(() => {
    if (!open || !isEnabled) {
      setRect(null);
      return;
    }
    function measure(): void {
      const node = acWrapRef.current;
      if (!node) return;
      const r = node.getBoundingClientRect();
      setRect({ top: r.bottom + 4, left: r.left, width: r.width });
    }
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open, isEnabled, acWrapRef, term]);

  const dropdown =
    open && isEnabled && rect && typeof document !== 'undefined'
      ? createPortal(
          <div
            className={styles.acDropdown}
            role="listbox"
            data-lookup-ac-dropdown
            style={{
              position: 'fixed',
              top: rect.top,
              left: rect.left,
              width: rect.width,
            }}
          >
            {isLoading ? (
              <div className={styles.acEmpty}>Searching…</div>
            ) : results.length === 0 ? (
              <div className={styles.acEmpty}>No customers match</div>
            ) : (
              results.map((c) => (
                <button
                  key={c.uuid}
                  type="button"
                  role="option"
                  aria-selected="false"
                  className={styles.acItem}
                  onMouseDown={(e) => {
                    // Prevent the input's blur from closing the dropdown
                    // before the click handler fires.
                    e.preventDefault();
                  }}
                  onClick={() => onSelect(c)}
                >
                  <span className={styles.acPrimary}>{c.full_name || c.name}</span>
                  <span className={styles.acSecondary}>
                    {c.phone}
                    {c.email ? ` · ${c.email}` : ''}
                  </span>
                </button>
              ))
            )}
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <Card>
        <div className={styles.searchRow}>
          <div className={styles.acWrap} ref={acWrapRef}>
            <Input
              label="Search for customer"
              placeholder="Search by name, email or phone…"
              value={term}
              onChange={(e) => {
                setTerm(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              autoComplete="off"
              aria-label="Customer search"
            />
            {dropdown}
          </div>
        </div>
      </Card>

      {customer === null && !customerLoading ? (
        <Card>
          <div className={styles.empty}>
            <User size={32} aria-hidden />
            <h3>Search for a customer to view their record</h3>
            <p>Type a name, phone number or email above. We'll show their full booking history.</p>
          </div>
        </Card>
      ) : null}

      {customerLoading ? (
        <Card>
          <div className={styles.empty}>
            <Spinner size="md" />
            <p style={{ marginTop: '0.5rem' }}>Loading customer…</p>
          </div>
        </Card>
      ) : null}

      {customer !== null ? (
        <Card>
          <div className={styles.custStats}>
            <div className={styles.statTile}>
              <div className={`${styles.statValue} ${styles.statValueTotal}`}>
                {bookingsTotal}
              </div>
              <div className={styles.statLabel}>Bookings</div>
            </div>
            <div className={styles.statTile}>
              <div className={`${styles.statValue} ${styles.statValueDone}`}>
                {stats.completed}
              </div>
              <div className={styles.statLabel}>Completed</div>
            </div>
            <div className={styles.statTile}>
              <div className={`${styles.statValue} ${styles.statValuePend}`}>
                {stats.pending}
              </div>
              <div className={styles.statLabel}>Pending</div>
            </div>
            <div className={styles.statTile}>
              <div className={`${styles.statValue} ${styles.statValueCanc}`}>
                {stats.cancelled}
              </div>
              <div className={styles.statLabel}>Cancelled</div>
            </div>
            <div className={styles.statTile}>
              <div className={`${styles.statValue} ${styles.statValueRev}`}>
                {formatMoney(stats.revenue)}
              </div>
              <div className={styles.statLabel}>Revenue</div>
            </div>
          </div>

          <div className={styles.custLayout}>
            <div className={styles.custSidebar}>
              <Card title="Personal details">
                <div className={styles.fieldGrid} style={{ gridTemplateColumns: '1fr' }}>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Name</span>
                    <span className={styles.fieldValue}>
                      {customer.full_name || customer.name}
                    </span>
                  </div>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Phone</span>
                    <span className={styles.fieldValue}>{customer.phone}</span>
                  </div>
                  {customer.phone2 ? (
                    <div className={styles.field}>
                      <span className={styles.fieldLabel}>Alt phone</span>
                      <span className={styles.fieldValue}>{customer.phone2}</span>
                    </div>
                  ) : null}
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Email</span>
                    <span className={styles.fieldValue}>{customer.email ?? '—'}</span>
                  </div>
                </div>
              </Card>

              <Card title="Address">
                <div className={styles.fieldGrid} style={{ gridTemplateColumns: '1fr' }}>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Address</span>
                    <span className={styles.fieldValue}>
                      {customer.effective_address.address || '—'}
                    </span>
                  </div>
                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Postcode</span>
                    <span className={styles.fieldValue}>
                      {customer.effective_address.postcode || '—'}
                    </span>
                  </div>
                </div>
              </Card>

              {customer.account_name || customer.account_number || customer.sort_code ? (
                <Card title="Banking">
                  <div className={styles.fieldGrid} style={{ gridTemplateColumns: '1fr' }}>
                    {customer.account_name ? (
                      <div className={styles.field}>
                        <span className={styles.fieldLabel}>Account name</span>
                        <span className={styles.fieldValue}>{customer.account_name}</span>
                      </div>
                    ) : null}
                    {customer.account_number ? (
                      <div className={styles.field}>
                        <span className={styles.fieldLabel}>Account number</span>
                        <span className={styles.fieldValueMono}>
                          {customer.account_number}
                        </span>
                      </div>
                    ) : null}
                    {customer.sort_code ? (
                      <div className={styles.field}>
                        <span className={styles.fieldLabel}>Sort code</span>
                        <span className={styles.fieldValueMono}>{customer.sort_code}</span>
                      </div>
                    ) : null}
                  </div>
                </Card>
              ) : null}

              <Button
                variant="secondary"
                icon={ExternalLink}
                onClick={() => navigate(`/customers/${encodeURIComponent(customer.uuid)}`)}
              >
                Open customer record
              </Button>
            </div>

            <div>
              <Card
                title={`Booking history${
                  bookingsLoading ? ' (loading…)' : ` (last ${bookings.length} of ${bookingsTotal})`
                }`}
                flush
              >
                <Table
                  columns={historyColumns}
                  data={bookings.slice(0, CUSTOMER_BOOKINGS_PAGE_SIZE)}
                  keyField={(b) => b.uuid}
                  loading={bookingsLoading}
                  emptyState={
                    <div className={styles.empty}>
                      <h3>No bookings yet</h3>
                      <p>This customer has no booking history on record.</p>
                    </div>
                  }
                  ariaLabel="Customer booking history"
                />
              </Card>
            </div>
          </div>
        </Card>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Booking Search tab — free-text search across bookings / pending / receipts.
// Backed by the new /collections/search/bookings endpoint. Each result row
// adapts its action buttons to the record_type.
// ---------------------------------------------------------------------------

interface BookingSearchTabProps {
  input: string;
  setInput: (v: string) => void;
  submitted: string;
  includeCompleted: boolean;
  setIncludeCompleted: (v: boolean) => void;
  isLoading: boolean;
  isError: boolean;
  results: BookingSearchResult[] | null;
  onSubmit: (e: React.FormEvent) => void;
  navigate: ReturnType<typeof useNavigate>;
}

const RECORD_TYPE_LABEL: Record<BookingSearchResult['record_type'], string> = {
  booking: 'Booking',
  pending: 'Pending',
  receipt: 'Receipt',
};

const BOOKING_SEARCH_STATUS_VARIANT: Record<string, BadgeVariant> = {
  pending: 'warning',
  in_progress: 'info',
  completed: 'success',
  cancelled: 'danger',
  cancellation_requested: 'warning',
  final_notice: 'danger',
  booked: 'success',
  pending_review: 'warning',
  approved: 'success',
  rejected: 'danger',
  archived: 'muted',
};

function snippetFromResult(row: BookingSearchResult): string | null {
  return row.notes ?? row.driver_notes ?? row.additional_information ?? null;
}

function BookingSearchTab(props: BookingSearchTabProps): JSX.Element {
  const {
    input,
    setInput,
    submitted,
    includeCompleted,
    setIncludeCompleted,
    isLoading,
    isError,
    results,
    onSubmit,
    navigate,
  } = props;

  const trimmed = input.trim();
  const minNotReached = trimmed.length > 0 && trimmed.length < 2;
  const hasSubmitted = submitted.length > 0;
  const noResults =
    hasSubmitted && !isLoading && results !== null && results.length === 0;

  function handleOpenResult(row: BookingSearchResult): void {
    if (row.record_type === 'booking') {
      navigate(`/collections/bookings/${encodeURIComponent(row.enc_id)}`);
      return;
    }
    if (row.record_type === 'pending') {
      navigate(`/collections/pending/${encodeURIComponent(row.enc_id)}`);
      return;
    }
    navigate(`/collections/receipts?booking_enc_id=${encodeURIComponent(row.enc_id)}`);
  }

  return (
    <>
      <Card>
        <form onSubmit={onSubmit} className={styles.searchRow}>
          <Input
            label="Search notes, driver notes and receipt comments"
            placeholder="Enter at least 2 characters…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            aria-label="Booking notes search"
            autoFocus
          />
          <Button
            type="submit"
            variant="primary"
            icon={SearchIcon}
            disabled={trimmed.length < 2}
          >
            Search
          </Button>
        </form>
        <label className={styles.toggleRow}>
          <input
            type="checkbox"
            checked={includeCompleted}
            onChange={(e) => setIncludeCompleted(e.target.checked)}
          />
          <span>Include completed &amp; cancelled records</span>
        </label>
        {minNotReached ? (
          <div className={styles.hint}>Type at least 2 characters to search.</div>
        ) : null}
      </Card>

      {!hasSubmitted ? (
        <Card>
          <div className={styles.empty}>
            <MessageSquare size={32} aria-hidden />
            <h3>Search across booking notes</h3>
            <p>
              We&apos;ll look through bookings, pending records and receipts for any
              matching free-text.
            </p>
          </div>
        </Card>
      ) : null}

      {isLoading ? (
        <Card>
          <div className={styles.empty}>
            <Spinner size="md" />
            <p style={{ marginTop: '0.5rem' }}>Searching…</p>
          </div>
        </Card>
      ) : null}

      {isError ? (
        <Card>
          <div className={styles.empty}>
            <h3>Search failed</h3>
            <p>The request failed. Please try again.</p>
          </div>
        </Card>
      ) : null}

      {noResults ? (
        <Card>
          <div className={styles.empty}>
            <h3>No matches found</h3>
            <p>
              No records mention <strong>{submitted}</strong>. Try a different term or
              include completed &amp; cancelled records.
            </p>
          </div>
        </Card>
      ) : null}

      {results !== null && results.length > 0 ? (
        <Card flush>
          <ul className={styles.notesList} aria-label="Booking search results">
            {results.map((row) => {
              const snippet = snippetFromResult(row);
              const statusVariant =
                BOOKING_SEARCH_STATUS_VARIANT[row.status] ?? 'default';
              return (
                <li key={`${row.record_type}-${row.enc_id}`} className={styles.notesItem}>
                  <div className={styles.notesItemHeader}>
                    <span className={styles.notesItemCustomer}>
                      {row.customer_name}
                    </span>
                    <Badge
                      variant={
                        row.record_type === 'booking'
                          ? 'info'
                          : row.record_type === 'pending'
                            ? 'warning'
                            : 'success'
                      }
                    >
                      {RECORD_TYPE_LABEL[row.record_type]}
                    </Badge>
                    <Badge variant={statusVariant}>
                      {row.status.replaceAll('_', ' ')}
                    </Badge>
                    {row.collection_date ? (
                      <span className={styles.notesItemMeta}>
                        {formatDate(row.collection_date)}
                      </span>
                    ) : null}
                  </div>
                  {snippet ? (
                    <div className={styles.notesSnippet}>{snippet}</div>
                  ) : null}
                  <div className={styles.actionsRow}>
                    {row.record_type === 'booking' ? (
                      <>
                        <Button
                          variant="secondary"
                          size="sm"
                          icon={Pencil}
                          onClick={() =>
                            navigate(`/collections/bookings/${encodeURIComponent(row.enc_id)}/edit`)
                          }
                        >
                          Edit
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          icon={ExternalLink}
                          onClick={() => handleOpenResult(row)}
                        >
                          View
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="primary"
                        size="sm"
                        icon={ExternalLink}
                        onClick={() => handleOpenResult(row)}
                      >
                        View
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      ) : null}
    </>
  );
}
